/**
 * Elite Prime floating “Prime Assistant” chip — opens WhatsApp CTA.
 *
 * Belongs here: FAB chrome + WhatsApp handoff for the Prime sell tab.
 * Does not belong here: About Elite video FAB (Elite tab only).
 */
import { useLayoutEffect, useState, type RefObject } from 'react'
import { motion } from 'framer-motion'
import {
  ELITE_ABOUT_FAB_GAP_PX,
  readEliteAboutFabSafeBottomPx
} from '../../../components/elite/eliteAboutFabLayout'
import { openWhatsapp } from '../../../utils/appBridge'
import { ElitePlansEventId, trackElitePlansClick } from '../analytics'
import {
  PRIME_ASSISTANT_ICON_SRC,
  PRIME_ASSISTANT_WHATSAPP_FAB_URL,
} from '../constants/primeAssistant'

const PRIME_ASSISTANT_HALO_SRC = '/images/elite-plans/elite-prime/fab/prime-assistant-halo.svg'
/** Stop retrying CTA attach after ~1s of animation frames. */
const MAX_CTA_ATTACH_FRAMES = 60
/** Fallback when live measure fails — tall enough to clear consent sticky CTAs. */
const FALLBACK_ELITE_CTA_HEIGHT_PX = 220
/** Give layout/fonts a short window before falling back. */
const MEASURE_FALLBACK_TIMEOUT_MS = 400

function readElementHeightPx(el: HTMLElement | null | undefined): number {
  if (!el) return 0
  try {
    const rectHeight = el.getBoundingClientRect()?.height ?? 0
    if (rectHeight > 0) return Math.ceil(rectHeight)
    return el.offsetHeight ?? 0
  } catch {
    return 0
  }
}

function readResizeObserverHeightPx(entry: ResizeObserverEntry | undefined, el: HTMLElement): number {
  try {
    const box = entry?.borderBoxSize
    if (box) {
      const size = Array.isArray(box) ? box[0] : box
      if (size?.blockSize > 0) return Math.ceil(size.blockSize)
    }
  } catch {
    // fall through to element measure
  }
  return readElementHeightPx(el)
}

type ElitePrimeAssistantFabProps = {
  /** When false, FAB stays mounted but hidden. */
  showFab?: boolean
  isEliteCtaVisible?: boolean
  /** Floating Elite Prime CTA element — its measured height lifts the FAB. */
  eliteCtaRef?: RefObject<HTMLElement>
  /**
   * Bottom offset of the Elite CTA container (distance from viewport bottom).
   * Defaults to safe-area bottom padding.
   */
  eliteCtaBottomPx?: number
}

export function ElitePrimeAssistantFab({
  showFab = false,
  isEliteCtaVisible = false,
  eliteCtaRef,
  eliteCtaBottomPx
}: ElitePrimeAssistantFabProps) {
  const [eliteCtaHeightPx, setEliteCtaHeightPx] = useState(0)
  const safeBottomPx = readEliteAboutFabSafeBottomPx()
  const ctaBottomPx = Number.isFinite(eliteCtaBottomPx) ? (eliteCtaBottomPx as number) : safeBottomPx
  const bottomPx =
    isEliteCtaVisible && eliteCtaHeightPx > 0
      ? ctaBottomPx + eliteCtaHeightPx + ELITE_ABOUT_FAB_GAP_PX
      : safeBottomPx + ELITE_ABOUT_FAB_GAP_PX

  useLayoutEffect(() => {
    if (!showFab || !isEliteCtaVisible) {
      setEliteCtaHeightPx(0)
      return
    }

    let cancelled = false
    let measuredSuccessfully = false
    let resizeObserver: ResizeObserver | null = null
    let rafId = 0
    let measureRafId = 0
    let framesTried = 0

    const applyHeight = (nextHeightPx: number, isFallback = false) => {
      if (cancelled || !Number.isFinite(nextHeightPx) || nextHeightPx <= 0) return
      if (!isFallback) measuredSuccessfully = true
      if (isFallback && measuredSuccessfully) return
      setEliteCtaHeightPx((prev) => (prev === nextHeightPx ? prev : nextHeightPx))
    }

    const applyFallback = () => {
      applyHeight(FALLBACK_ELITE_CTA_HEIGHT_PX, true)
    }

    const fallbackTimeoutId = window.setTimeout(() => {
      if (!cancelled && !measuredSuccessfully) applyFallback()
    }, MEASURE_FALLBACK_TIMEOUT_MS)

    const observe = (el: HTMLElement) => {
      if (!el?.isConnected) {
        applyFallback()
        return
      }

      applyHeight(readElementHeightPx(el))

      measureRafId = requestAnimationFrame(() => {
        if (cancelled) return
        applyHeight(readElementHeightPx(el))
      })

      if (typeof ResizeObserver === 'undefined') {
        if (!measuredSuccessfully) applyFallback()
        return
      }

      try {
        resizeObserver = new ResizeObserver((entries) => {
          const entry = entries?.[0]
          if (!entry) return
          applyHeight(readResizeObserverHeightPx(entry, el))
        })
        try {
          resizeObserver.observe(el, { box: 'border-box' })
        } catch {
          resizeObserver.observe(el)
        }
      } catch {
        if (!measuredSuccessfully) applyFallback()
      }
    }

    const attach = () => {
      if (cancelled) return
      const el = eliteCtaRef?.current ?? null
      if (!el) {
        framesTried += 1
        if (framesTried >= MAX_CTA_ATTACH_FRAMES) {
          applyFallback()
          return
        }
        rafId = requestAnimationFrame(attach)
        return
      }
      observe(el)
    }

    attach()

    return () => {
      cancelled = true
      cancelAnimationFrame(rafId)
      cancelAnimationFrame(measureRafId)
      window.clearTimeout(fallbackTimeoutId)
      try {
        resizeObserver?.disconnect()
      } catch {
        // ignore
      }
    }
  }, [showFab, isEliteCtaVisible, eliteCtaRef])

  const handleClick = () => {
    if (!showFab) return
    trackElitePlansClick(ElitePlansEventId.CtaClick, {
      planId: 'elite-prime',
      buttonLabel: 'Prime Assistant_fab'
    })
    try {
      openWhatsapp(PRIME_ASSISTANT_WHATSAPP_FAB_URL)
    } catch {
      // ignore — never crash the landing page from FAB tap
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, bottom: bottomPx }}
      animate={{
        opacity: showFab ? 1 : 0,
        y: showFab ? 0 : 12,
        bottom: bottomPx
      }}
      transition={{
        opacity: { type: 'tween', duration: showFab ? 0.35 : 0, ease: 'easeOut' },
        y: { type: 'tween', duration: showFab ? 0.35 : 0, ease: 'easeOut' },
        bottom: { type: 'tween', duration: 0.3, ease: 'easeOut' }
      }}
      aria-hidden={!showFab}
      className={`fixed right-5 z-[60] ${showFab ? 'pointer-events-auto' : 'pointer-events-none'}`}
    >
      <button
        type='button'
        onClick={handleClick}
        tabIndex={showFab ? 0 : -1}
        className="ep-focus-ring relative inline-flex items-center overflow-visible rounded-[42px] border-[1.5px] border-solid border-[#d89782] bg-white py-2 pl-2 pr-[11px] font-['Matter'] text-[12px] font-medium leading-[1.24] text-[#b85230]"
        aria-label='Chat with Prime Assistant'
      >
        {/* Soft outer rings from Figma — sit slightly outside the chip edge */}
        <img
          src={PRIME_ASSISTANT_HALO_SRC}
          alt=''
          aria-hidden='true'
          width={47}
          height={47}
          className='pointer-events-none absolute left-[-1.5px] top-[-4.5px] size-[47px] max-w-none'
        />
        <span className='relative flex items-center gap-2 px-1'>
          <img
            src={PRIME_ASSISTANT_ICON_SRC}
            alt=''
            aria-hidden='true'
            width={28}
            height={28}
            className='size-7 shrink-0 object-contain'
          />
          <span className='text-left'>
            <span className='block'>Prime</span>
            <span className='block'>Assistant</span>
          </span>
        </span>
      </button>
    </motion.div>
  )
}
